/*
 * BeForm / BeFormRow -- a shared label column and a shared trailing column.
 * Behaviour and provenance: layout/form.css, Notes/66 §1, Notes/69.
 */

import { type CSSProperties, createContext, type ReactNode, useContext } from "react";
import { type BeProps, beComponent } from "./create.tsx";
import { type BeSpace, space } from "./Stack.tsx";

/*
 * A row is `display: contents`, so outside a BeForm it does not fail -- it
 * DISSOLVES, and its three cells become children of whatever parent it found.
 * In a flex column that stacks label, control and trailing onto separate lines,
 * which is a layout bug with no error attached to it.
 *
 * The check has to be at runtime: the mistake reached the component editor
 * through a `NumberField` helper that returns a BeFormRow, so the call site
 * reads `<NumberField>` and no static scan of the JSX could see it.
 */
const InForm = createContext(false);

export type BeFormProps = BeProps<
	"div",
	{
		children?: ReactNode;
		/** Reserve a third column, so every row's trailing control shares a right edge. */
		trailing?: boolean;
		gap?: BeSpace;
		pad?: BeSpace;
	}
>;

export type BeFormRowProps = BeProps<
	"div",
	{
		/** Omit for a row that spans the whole width (a checkbox pair, a note). */
		label?: ReactNode;
		/** Goes in the shared trailing column -- a spinner, a unit, a reset button. */
		trailing?: ReactNode;
		children?: ReactNode;
	}
>;

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(" ");

export const BeForm = beComponent<BeFormProps, HTMLDivElement>("BeForm", (props, be) => {
	const { children, trailing, gap, pad, scale, className, style, ...rest } = props;
	const root = be("be-form", trailing && "has-trailing");
	const css =
		gap || pad
			? ({
					...root.style,
					...(gap ? { "--be-gap": space(gap) } : null),
					...(pad ? { "--be-pad": space(pad) } : null),
				} as CSSProperties)
			: root.style;

	return (
		<InForm.Provider value={true}>
			<div {...rest} {...root} style={css}>
				{children}
			</div>
		</InForm.Provider>
	);
});

export const BeFormRow = beComponent<BeFormRowProps, HTMLDivElement>("BeFormRow", (props, be) => {
	const { label, trailing, children, scale, className, style, ...rest } = props;

	if (!useContext(InForm)) {
		throw new Error(
			"<BeFormRow> must be inside a <BeForm>. It is `display: contents`, so outside one it dissolves and its label/control/trailing " +
				"become separate children of the surrounding container -- silently stacking onto different lines. Wrap them in <BeForm>.",
		);
	}

	return (
		<div {...rest} {...be("be-form__row")}>
			{label != null && <span className="be-form__label">{label}</span>}
			<div className={cx("be-form__control", label == null && "is-full", label != null && trailing == null && "is-span")}>{children}</div>
			{trailing != null && <div className="be-form__trailing">{trailing}</div>}
		</div>
	);
});
